import type { CSSProperties, ReactNode } from "react";
import { GRAPH_INFO, type GraphInfoKey } from "./chartInfoCopy";
import "@/styles/v47-pages.css";

/**
 * The information icon on a chart (prototype v45 `graphInfo`): an "i" that shows what the chart
 * plots on hover or keyboard focus. A chart gets it in its own top right corner: put it first
 * inside the chart's container and give the container `gwrap` (position: relative), or use
 * `ChartBox`. A table or a list has content in that corner already, so it goes beside the heading
 * instead (`inline`, inside the `h2`). A span, not a button, because some charts sit inside a
 * clickable card (Home's net worth panel) and buttons can't nest.
 */
export function ChartInfo({ k, inline = false }: { k: GraphInfoKey; inline?: boolean }) {
  const text = GRAPH_INFO[k];
  return (
    <span
      className={`ginf${inline ? " inl" : ""}`}
      tabIndex={0}
      role="note"
      aria-label={`What this graph is showing: ${text}`}
      data-testid={`ginf-${k}`}
      onClick={(e) => e.stopPropagation()}
    >
      <span aria-hidden="true">i</span>
      <span className="gtip" aria-hidden="true">
        {text}
      </span>
    </span>
  );
}

/** A chart's container with its id (the prototype's, kept for the page guides) and its icon. */
export function ChartBox({
  id,
  info,
  children,
  className,
  style,
}: {
  id: GraphInfoKey | string;
  /** Which explanation; defaults to the id. */
  info?: GraphInfoKey;
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
}) {
  const key = info ?? (id in GRAPH_INFO ? (id as GraphInfoKey) : undefined);
  return (
    <div id={id} className={`gwrap${className ? ` ${className}` : ""}`} style={style}>
      {key && <ChartInfo k={key} />}
      {children}
    </div>
  );
}
